'use client'

import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Receipt, RefreshCcw } from 'lucide-react'

import type { SelectInvoice, SelectPrice } from '@package/drizzle/schema'
import { Button, Skeleton, toast, Tooltip, TooltipContent, TooltipTrigger } from '@ui/web/components'
import { XENDIT_EXPIRY_INVOICE_IN_HR_ADD_ON, XENDIT_EXPIRY_INVOICE_IN_HR_NEW } from '@helper/utils/core'
import { getDaysBetween, offsetDate } from '@helper/utils/date'
import { buildUrl, formatCurrency, formatDate } from '@helper/utils/format'
import { xenditPaymentMethod } from '@helper/utils/xendit'
import cn from '@config/tailwind/cn'

import { useAuthClient, useSharedTRPC } from '../context'

type TransactionDetailOptions =
  | {
      type: 'school'
    }
  | {
      type: 'web'
    }
  | {
      type: 'admin'
    }

interface TransactionDetailProps {
  data: SelectInvoice & { price?: SelectPrice | null }
  options: TransactionDetailOptions
  className?: string
}

export function TransactionDetail({ data, options, className }: TransactionDetailProps) {
  const authClient = useAuthClient()

  const trpc = useSharedTRPC()

  /**
   * A renewal's two parts, recovered from the invoice the same way Detail Tagihan and the PDF do:
   * the term is `perSeatPrice x seats x duration`, and whatever `totalPrice` carries beyond it is
   * the empty seats' pro-rata bridge — seats that started at payment rather than at the handover.
   * Zero for every renewal made before that existed, and for any other type.
   */
  const renewalTermTotal =
    data.type === 'RENEWAL' && data.price ? data.perSeatPrice * data.seats * data.price.duration : 0
  const renewalBridgeTotal = data.type === 'RENEWAL' && data.price ? Math.max(0, data.totalPrice - renewalTermTotal) : 0
  const renewalBridgeFrom = data.paidDate ?? data.createdAt
  const renewalBridgeUntil = data.customMembershipStartDate

  const {
    data: activeMemberRole,
    isPending: isPendingActiveMemberRole,
    error: errorActiveMemberRole,
    refetch: refetchActiveMemberRole,
  } = authClient.useActiveMemberRole()

  const {
    data: getPdfData,
    isLoading: isLoadingGetPdfData,
    error: errorGetPdfData,
    refetch: refetchGetPdfData,
  } = useQuery({ ...trpc.invoice.getPdf.queryOptions({ invoiceId: data.id }), staleTime: 1000 * 60 * 60 })

  const handleDownload = React.useCallback(() => {
    if (!getPdfData) return

    // Convert base64 to blob and download
    const byteCharacters = atob(getPdfData.pdf)
    const byteArray = new Uint8Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i)
    }
    const blob = new Blob([byteArray], { type: 'application/pdf' })

    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `PiBo_${data.invoiceNumber}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  }, [getPdfData])

  const receiptQuery = useQuery({
    ...trpc.invoice.getReceiptUrl.queryOptions({ invoiceId: data.id }),
    enabled: false,
  })

  const handleViewReceipt = React.useCallback(async () => {
    const res = await receiptQuery.refetch()
    if (res.data?.url) window.open(res.data.url, '_blank', 'noopener,noreferrer')
    else toast.error('Receipt tidak tersedia')
  }, [receiptQuery])

  const error = errorActiveMemberRole ?? errorGetPdfData

  const isPending = isPendingActiveMemberRole || isLoadingGetPdfData

  if (error) {
    return (
      <div
        className={cn(
          'srd:flex srd:flex-col srd:gap-y-4 srd:items-center srd:justify-center srd:text-center',
          className,
        )}
      >
        <p className='srd:text-sm srd:text-main-red-100'>{error.message}</p>

        <Button
          variant='outline'
          onClick={async () => {
            if (errorActiveMemberRole) await refetchActiveMemberRole()
            if (errorGetPdfData) await refetchGetPdfData()
          }}
        >
          <RefreshCcw className='srd:size-4 srd:mr-2 srd:text-main-blue-100' />
          Coba lagi
        </Button>
      </div>
    )
  }

  if (isPending) {
    return (
      <div className={cn('srd:flex srd:flex-col srd:gap-y-4', className)}>
        <Skeleton className='srd:w-full srd:h-8 srd:rounded-full' />
        <Skeleton className='srd:w-full srd:max-w-[90%] srd:h-6 srd:rounded-full' />
        <Skeleton className='srd:w-full srd:max-w-[50%] srd:h-5 srd:rounded-full' />
        <Skeleton className='srd:w-full srd:max-w-[50%] srd:h-5 srd:rounded-full' />
        <Skeleton className='srd:w-full srd:h-8 srd:rounded-full' />
      </div>
    )
  }

  return (
    <section className={cn('srd:flex srd:flex-col srd:gap-y-4', className)}>
      <section className='srd:flex srd:flex-col srd:gap-y-2 srd:text-neutrals-black srd:mt-4'>
        <section className='srd:flex srd:flex-col srd:gap-y-2'>
          <h6 className='srd:font-bold srd:text-xl'>
            {data.type === 'ADD-ON'
              ? 'Tambah Kursi'
              : data.type === 'RENEWAL'
                ? `Perpanjangan (${data.price?.duration ?? 'X'} Bulan)`
                : data.type === 'DIRECT_PAID'
                  ? 'Premium (Langsung)'
                  : `Premium (${data.price?.duration ?? 'X'} Bulan)`}
          </h6>

          <p className='srd:text-base srd:font-normal srd:text-neutrals-n-75'>
            {(() => {
              if (data.type === 'DIRECT_PAID') {
                if (data.membershipStartDate && data.membershipEndDate) {
                  const startDate = formatDate(data.membershipStartDate, { dateStyle: 'long' })
                  const endDate = formatDate(data.membershipEndDate, { dateStyle: 'long' })
                  return `${startDate} - ${endDate}`
                }
                return 'Informasi tidak tersedia'
              }

              if (data.type === 'ADD-ON') {
                if (data.paidDate && data.membershipEndDate) {
                  const endDate = getDaysBetween(data.paidDate, data.membershipEndDate)

                  return `Pro-rata ${formatCurrency(data.perSeatPrice)} x ${endDate.totalDays} hari`
                }

                return 'Informasi tidak tersedia'
              }

              if (data.membershipEndDate && data.price) {
                if (data.membershipStartDate) {
                  const startDate = formatDate(data.membershipStartDate, { dateStyle: 'long' })
                  const endDate = formatDate(offsetDate(data.membershipStartDate, { months: data.price.duration }), {
                    dateStyle: 'long',
                  })

                  return `${startDate} - ${endDate}`
                }

                if (data.customMembershipStartDate) {
                  const startDate = formatDate(data.customMembershipStartDate, { dateStyle: 'long' })
                  const endDate = formatDate(
                    offsetDate(data.customMembershipStartDate, { months: data.price.duration }),
                    {
                      dateStyle: 'long',
                    },
                  )

                  return `${startDate} - ${endDate}`
                }

                return 'Informasi tidak tersedia'
              }

              return 'Informasi tidak tersedia'
            })()}
          </p>
        </section>

        <section className='srd:flex srd:flex-col srd:gap-y-2 srd:border-t srd:border-neutrals-n-20 srd:pt-2'>
          <section className='srd:flex srd:gap-x-6 srd:flex-wrap srd:gap-y-2'>
            <section className='srd:flex srd:flex-col srd:gap-y-1'>
              <p className='srd:font-medium srd:text-xs srd:text-neutrals-n-75'>Tanggal Pesan</p>
              <p className='srd:font-normal srd:text-base srd:text-neutrals-black'>
                {formatDate(data.createdAt, { dateStyle: 'long' })}
              </p>
            </section>

            <section className='srd:flex srd:flex-col srd:gap-y-1'>
              <p className='srd:font-medium srd:text-xs srd:text-neutrals-n-75'>Nomor Tagihan</p>
              <p className='srd:font-normal srd:text-base srd:text-neutrals-black'>{data.invoiceNumber}</p>
            </section>
          </section>

          <section className='srd:flex srd:flex-col srd:gap-y-1'>
            <p className='srd:font-medium srd:text-xs srd:text-neutrals-n-75'>Status</p>
            <p className='srd:font-normal srd:text-base srd:text-neutrals-black'>
              {data.status === 'PAID' ? 'Lunas' : data.status === 'EXPIRED' ? 'Dibatalkan' : 'Belum bayar'}
            </p>
          </section>

          <section className='srd:flex srd:flex-col srd:gap-y-1'>
            <p className='srd:font-medium srd:text-xs srd:text-neutrals-n-75'>Detail Pembayaran</p>
            <p className='srd:font-normal srd:text-base srd:text-neutrals-black'>
              {data.paymentMethod === 'OFFLINE'
                ? 'Pembayaran Offline'
                : data.paymentMethod
                  ? xenditPaymentMethod(data.paymentMethod)
                  : '-'}
            </p>
          </section>
        </section>

        <section className='srd:flex srd:flex-col srd:gap-y-2 srd:border-t srd:border-neutrals-n-20 srd:pt-2'>
          <section className='srd:flex srd:items-center srd:justify-between srd:gap-x-4 srd:flex-wrap srd:gap-y-2'>
            <p className='srd:font-medium srd:text-base srd:text-neutrals-black'>Total Kursi</p>

            <p className='srd:font-normal srd:text-base srd:text-neutrals-black'>{data.seats}</p>
          </section>
        </section>

        {/* Renewal with empty seats that started at payment: the term and the pro-rata bridge
            are two lines, as on Detail Tagihan, or the total would not follow from the seat count. */}
        {renewalBridgeTotal > 0 && (
          <section className='srd:flex srd:flex-col srd:gap-y-2 srd:border-t srd:border-neutrals-n-20 srd:pt-2'>
            <section className='srd:flex srd:items-center srd:justify-between srd:gap-x-4 srd:flex-wrap srd:gap-y-2'>
              <p className='srd:font-medium srd:text-base srd:text-neutrals-black'>Perpanjangan</p>

              <p className='srd:font-normal srd:text-base srd:text-neutrals-black'>
                {formatCurrency(renewalTermTotal)}
              </p>
            </section>

            <section className='srd:flex srd:items-start srd:justify-between srd:gap-x-4 srd:flex-wrap srd:gap-y-2'>
              <section className='srd:flex srd:flex-col srd:gap-y-0.5'>
                <p className='srd:font-medium srd:text-base srd:text-neutrals-black'>Kursi kosong aktif lebih awal</p>
                <p className='srd:font-normal srd:text-sm srd:text-neutrals-n-75'>
                  Pro-rata {formatDate(renewalBridgeFrom, { dateStyle: 'long' })}
                  {renewalBridgeUntil ? ` - ${formatDate(renewalBridgeUntil, { dateStyle: 'long' })}` : ''}
                </p>
              </section>

              <p className='srd:font-normal srd:text-base srd:text-neutrals-black'>
                {formatCurrency(renewalBridgeTotal)}
              </p>
            </section>

            {!!data.totalDiscount && data.totalDiscount > 0 && (
              <section className='srd:flex srd:items-center srd:justify-between srd:gap-x-4 srd:flex-wrap srd:gap-y-2'>
                <p className='srd:font-medium srd:text-base srd:text-neutrals-black'>Diskon promo</p>

                <p className='srd:font-normal srd:text-base srd:text-neutrals-black'>
                  ({formatCurrency(data.totalDiscount)})
                </p>
              </section>
            )}
          </section>
        )}

        <section className='srd:flex srd:flex-col srd:gap-y-2 srd:border-t srd:border-neutrals-n-20 srd:pt-2'>
          <section className='srd:flex srd:items-center srd:justify-between srd:gap-x-4 srd:flex-wrap srd:gap-y-2'>
            <p className='srd:font-medium srd:text-base srd:text-neutrals-black'>Total</p>

            <p className='srd:font-medium srd:text-base srd:text-neutrals-black'>{formatCurrency(data.amountPaid)}</p>
          </section>
        </section>
      </section>

      <section className='srd:mt-8 srd:flex srd:flex-col srd:gap-y-4'>
        {data.status === 'PENDING' && options.type !== 'admin' && (
          <p className='srd:text-xs srd:font-neutrals-n-75 srd:font-normal srd:text-left'>
            Pesanan batal otomatis jika tidak dibayar dalam{' '}
            {data.type === 'ADD-ON' ? XENDIT_EXPIRY_INVOICE_IN_HR_ADD_ON : XENDIT_EXPIRY_INVOICE_IN_HR_NEW} jam.
          </p>
        )}

        {data.status === 'PAID' && options.type === 'school' && activeMemberRole?.role === 'member' && (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button baseButton variant='outline'>
                  <Receipt className='srd:size-5 srd:mr-2' />
                  <p>Tanda Terima</p>
                </Button>
              }
            />
            <TooltipContent>
              <p>Silahkan hubungi admin untuk mencetak tanda terima</p>
            </TooltipContent>
          </Tooltip>
        )}

        {data.status === 'PAID' &&
          ((options.type === 'school' && activeMemberRole?.role !== 'member') || options.type === 'web') &&
          getPdfData && (
            <Button
              loading={isLoadingGetPdfData}
              variant='outline'
              disabled={isLoadingGetPdfData}
              onClick={handleDownload}
            >
              <Receipt className='srd:size-5 srd:mr-2' />
              <p>Tanda Terima</p>
            </Button>
          )}

        {data.type === 'DIRECT_PAID' &&
          data.status === 'PAID' &&
          data.receiptFileId &&
          (options.type === 'school' && activeMemberRole?.role === 'member' ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button baseButton variant='outline'>
                    <Receipt className='srd:size-5 srd:mr-2' />
                    <p>Bukti Pembayaran</p>
                  </Button>
                }
              />
              <TooltipContent>
                <p>Silahkan hubungi admin untuk melihat bukti pembayaran</p>
              </TooltipContent>
            </Tooltip>
          ) : (options.type === 'school' && activeMemberRole?.role !== 'member') || options.type === 'web' ? (
            <Button
              variant='outline'
              loading={receiptQuery.isFetching}
              disabled={receiptQuery.isFetching}
              onClick={handleViewReceipt}
            >
              <Receipt className='srd:size-5 srd:mr-2' />
              <p>Bukti Pembayaran</p>
            </Button>
          ) : null)}

        {data.status === 'EXPIRED' && options.type === 'school' && activeMemberRole?.role === 'member' && (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button baseButton>
                  <p>Pesan Lagi</p>
                </Button>
              }
            />

            <TooltipContent>
              <p>Silahkan hubungi admin untuk memesan kembali</p>
            </TooltipContent>
          </Tooltip>
        )}

        {data.status === 'EXPIRED' &&
          data.type !== 'DIRECT_PAID' &&
          ((options.type === 'school' && activeMemberRole?.role !== 'member') || options.type === 'web') && (
            <Button asChild>
              <a
                href={
                  buildUrl(
                    options.type === 'school' ? 'school' : 'web',
                    `/checkout/${data.type === 'ADD-ON' ? 'add-on' : data.type === 'RENEWAL' ? 'perpanjang' : 'new'}`,
                    {
                      premium_seats: data.seats.toString(),
                    },
                  ).href
                }
                target='_blank'
              >
                <p>Pesan Lagi</p>
              </a>
            </Button>
          )}

        {data.status === 'EXPIRED' &&
          data.type === 'DIRECT_PAID' &&
          ((options.type === 'school' && activeMemberRole?.role !== 'member') || options.type === 'web') && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button baseButton>
                    <p>Pesan Lagi</p>
                  </Button>
                }
              />
              <TooltipContent>
                <p>Silahkan hubungi admin untuk memesan kembali</p>
              </TooltipContent>
            </Tooltip>
          )}

        {data.status === 'PENDING' && options.type === 'school' && activeMemberRole?.role === 'member' && (
          <section className='srd:flex srd:sm:flex-row srd:flex-col-reverse srd:gap-x-4 srd:gap-y-2'>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button baseButton variant='ghost' className='srd:flex-1'>
                    <Receipt className='srd:size-5 srd:mr-2' />
                    <p>Surat Tagihan</p>
                  </Button>
                }
              />

              <TooltipContent>
                <p>Silahkan hubungi admin untuk mendapatkan surat tagihan</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger
                render={
                  <Button variant='gradient' baseButton className='srd:flex-1'>
                    <p>Bayar sekarang</p>
                  </Button>
                }
              />
              <TooltipContent>
                <p>Silahkan hubungi admin untuk membayar</p>
              </TooltipContent>
            </Tooltip>
          </section>
        )}

        {data.status === 'PENDING' &&
          ((options.type === 'school' && activeMemberRole?.role !== 'member') || options.type === 'web') &&
          getPdfData && (
            <section className='srd:flex srd:sm:flex-row srd:flex-col-reverse srd:gap-x-4 srd:gap-y-2'>
              <Button
                loading={isLoadingGetPdfData}
                variant='ghost'
                className='srd:flex-1'
                disabled={isLoadingGetPdfData}
                onClick={handleDownload}
              >
                <Receipt className='srd:size-5 srd:mr-2' />
                <p>Surat Tagihan</p>
              </Button>

              {data.paymentUrl ? (
                <Button asChild variant='gradient' className='srd:flex-1'>
                  <a href={data.paymentUrl}>
                    <p>Bayar sekarang</p>
                  </a>
                </Button>
              ) : (
                <Button
                  asChild
                  variant='gradient'
                  className='srd:flex-1'
                  onClick={() => toast.error('Tidak ditemukan URL pembayaran.')}
                >
                  <p>Bayar sekarang</p>
                </Button>
              )}
            </section>
          )}
      </section>
    </section>
  )
}
